/**
 * useOutputFiles - Hook for fetching execution output files
 *
 * Provides React Query hook for fetching output files from connector executions.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type { OutputFilesResponse, OutputFilesOptions } from '@/types/outputFiles';

const API_BASE = '/connectors';

/**
 * Fetch output files for a specific execution
 *
 * Backend endpoint: GET /connectors/{connector_type}/executions/{execution_id}/output-files
 */
async function fetchOutputFiles(
  connectorType: string,
  executionId: string,
  options?: OutputFilesOptions
): Promise<OutputFilesResponse> {
  const params: Record<string, any> = {
    include_download_urls: options?.include_download_urls ?? false,
    url_expiry_seconds: options?.url_expiry_seconds ?? 3600,
    include_schema: options?.include_schema ?? true,
    include_checksum: options?.include_checksum ?? false,
  };

  const response = await apiClient.get(
    `${API_BASE}/${connectorType}/executions/${executionId}/output-files`,
    { params }
  );

  return response.data;
}

/**
 * Hook to fetch output files for an execution
 *
 * @param connectorType - The type of connector (e.g., 'google_ads')
 * @param executionId - The execution ID to fetch files for
 * @param options - Optional configuration for the request
 * @param enabled - Whether to enable the query (default: true)
 *
 * @example
 * const { data, isLoading, error } = useOutputFiles(
 *   'google_ads',
 *   'exec-123',
 *   { include_download_urls: true, include_schema: true }
 * );
 */
export function useOutputFiles(
  connectorType: string,
  executionId: string,
  options?: OutputFilesOptions,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['outputFiles', connectorType, executionId, options],
    queryFn: () => fetchOutputFiles(connectorType, executionId, options),
    enabled: enabled && !!connectorType && !!executionId,
    staleTime: 60000, // 1 minute - files don't change after execution completes
  });
}
